import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Mail, Key, CheckCircle2, XCircle, Send, ExternalLink } from 'lucide-react';

export const AdminEmailSettingsPage = () => {
  const { api, user } = useAuth();
  const [settings, setSettings] = useState({
    sendgrid_configured: false,
    sendgrid_api_key_masked: '',
    sender_email: '',
    admin_notification_email: '',
  });
  const [form, setForm] = useState({
    sendgrid_api_key: '',
    sender_email: '',
    admin_notification_email: '',
  });
  const [testTo, setTestTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api().get('/admin/email-settings');
      setSettings(res.data);
      setForm({
        sendgrid_api_key: '',
        sender_email: res.data.sender_email || '',
        admin_notification_email: res.data.admin_notification_email || '',
      });
      setTestTo(user?.email || res.data.admin_notification_email || '');
    } catch (err) {
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (form.sendgrid_api_key.trim()) payload.sendgrid_api_key = form.sendgrid_api_key.trim();
      if (form.sender_email) payload.sender_email = form.sender_email;
      if (form.admin_notification_email) payload.admin_notification_email = form.admin_notification_email;
      await api().put('/admin/email-settings', payload);
      toast.success('Email settings saved');
      setForm(f => ({ ...f, sendgrid_api_key: '' }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo) return toast.error('Enter a test recipient');
    setTesting(true);
    try {
      const res = await api().post('/admin/email-settings/test', { to: testTo });
      if (res.data.sent) {
        toast.success(`Test email sent to ${testTo}! Check your inbox (and spam).`);
      } else if (!res.data.configured) {
        toast.error('SendGrid API key not configured. Save a key first.');
      } else {
        toast.error('Send failed — check backend logs and verify your sender address in SendGrid.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="text-zinc-400 p-8" data-testid="admin-email-loading">Loading…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-email-settings-page">
      <div>
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight text-white flex items-center gap-3">
          <Mail className="w-7 h-7 text-amber-500" /> Email Notifications
        </h1>
        <p className="text-zinc-400 text-sm mt-2">
          Configure SendGrid to send invoice, payment, inquiry, game assignment, and welcome emails.
        </p>
      </div>

      {/* Status card */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-lg p-5" data-testid="email-status-card">
        <div className="flex items-center gap-3">
          {settings.sendgrid_configured ? (
            <>
              <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              <div>
                <p className="font-semibold text-white">SendGrid is configured</p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  API key: <span className="font-mono text-zinc-400">{settings.sendgrid_api_key_masked}</span>
                </p>
              </div>
            </>
          ) : (
            <>
              <XCircle className="w-6 h-6 text-red-400" />
              <div>
                <p className="font-semibold text-white">SendGrid is NOT configured</p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Emails are being logged only. Paste your API key below to enable real delivery.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Settings form */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-lg p-5 space-y-4">
        <h2 className="font-semibold text-white text-lg flex items-center gap-2">
          <Key className="w-5 h-5 text-amber-500" /> API Configuration
        </h2>

        <div>
          <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">
            SendGrid API Key {settings.sendgrid_configured && <span className="text-zinc-600 normal-case ml-2">(leave blank to keep current)</span>}
          </label>
          <Input
            type="password"
            placeholder={settings.sendgrid_configured ? '••••••••••••' : 'SG.xxxxxxxxxxxxxxxx'}
            value={form.sendgrid_api_key}
            onChange={(e) => setForm(f => ({ ...f, sendgrid_api_key: e.target.value }))}
            className="bg-zinc-950 border-zinc-800 text-white font-mono"
            data-testid="email-api-key-input"
            autoComplete="off"
          />
          <p className="text-xs text-zinc-500 mt-1.5 flex items-center gap-1">
            Get your key at
            <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noreferrer" className="text-amber-400 hover:text-amber-300 inline-flex items-center gap-1">
              app.sendgrid.com/settings/api_keys <ExternalLink className="w-3 h-3" />
            </a>
            — create with <b>Mail Send → Full Access</b>.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">From Address</label>
            <Input
              type="email"
              value={form.sender_email}
              onChange={(e) => setForm(f => ({ ...f, sender_email: e.target.value }))}
              className="bg-zinc-950 border-zinc-800 text-white"
              data-testid="email-sender-input"
              placeholder="info@officialwiththewhistle.com"
            />
            <p className="text-xs text-zinc-500 mt-1">Must be a verified sender/domain in SendGrid.</p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">Admin Alert Recipient</label>
            <Input
              type="email"
              value={form.admin_notification_email}
              onChange={(e) => setForm(f => ({ ...f, admin_notification_email: e.target.value }))}
              className="bg-zinc-950 border-zinc-800 text-white"
              data-testid="email-admin-input"
              placeholder="info@officialwiththewhistle.com"
            />
            <p className="text-xs text-zinc-500 mt-1">Receives new "Need a Ref?" inquiry alerts.</p>
          </div>
        </div>

        <Button
          onClick={save}
          disabled={saving}
          className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
          data-testid="email-save-btn"
        >
          {saving ? 'Saving…' : 'Save Settings'}
        </Button>
      </div>

      {/* Test email */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-lg p-5 space-y-3">
        <h2 className="font-semibold text-white text-lg flex items-center gap-2">
          <Send className="w-5 h-5 text-amber-500" /> Send Test Email
        </h2>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.com"
            className="bg-zinc-950 border-zinc-800 text-white flex-1"
            data-testid="email-test-to-input"
          />
          <Button
            onClick={sendTest}
            disabled={testing || !settings.sendgrid_configured}
            className="bg-amber-500 hover:bg-amber-400 text-black font-bold disabled:opacity-40"
            data-testid="email-test-send-btn"
          >
            {testing ? 'Sending…' : 'Send Test'}
          </Button>
        </div>
        {!settings.sendgrid_configured && (
          <p className="text-xs text-amber-400/80">Save a SendGrid API key first to enable testing.</p>
        )}
      </div>

      {/* Email events info */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-5">
        <h2 className="font-semibold text-white text-sm mb-3 uppercase tracking-wider">Automatic Email Triggers</h2>
        <ul className="space-y-2 text-sm text-zinc-400">
          <li className="flex items-start gap-2"><span className="text-amber-500">→</span> New user registration → welcome email</li>
          <li className="flex items-start gap-2"><span className="text-amber-500">→</span> Invoice created → sent to client</li>
          <li className="flex items-start gap-2"><span className="text-amber-500">→</span> Stripe payment completed → receipt to client</li>
          <li className="flex items-start gap-2"><span className="text-amber-500">→</span> "Need a Ref?" inquiry → alert to admin</li>
          <li className="flex items-start gap-2"><span className="text-amber-500">→</span> Referee assigned to a game → notification to referee</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminEmailSettingsPage;
